import { MiscModelSplitsData } from '../../../lib/supabase/misc-payments/supabaseMiscModelSplitsService';

export type SplitType = 'none' | 'all' | 'uk' | 'fp' | 'gg_swap' | 'custom';

export interface SplitCounts {
  set1Count: number;
  set2Count: number;
  totalModels: number;
}

export interface SplitAmounts {
  set1Amount: number;
  set1Bonus: number;
  set2Amount: number;
  set2Bonus: number;
}

// Helper function to get active models by criteria
export const getActiveModels = (models: any[], pay_class?: 'UK' | 'FP') => {
  return models.filter(model =>
    model.account_type === 'Normal' &&
    !model.paused &&
    (pay_class ? model.pay_class === pay_class : true)
  );
};

// Calculate model counts per set for different split types
// Now uses saved splits if available, falls back to live model counts
export const calculateSplitCounts = (
  models: any[],
  splitType: SplitType,
  savedSplits?: MiscModelSplitsData | null
): SplitCounts => {
  if (splitType === 'none') return { set1Count: 0, set2Count: 0, totalModels: 0 };

  // Try to use saved splits first
  if (savedSplits) {
    let splitData: { 1: number; 2: number } | undefined;

    switch (splitType) {
      case 'all':
        splitData = savedSplits.all_models;
        break;
      case 'uk':
        splitData = savedSplits.uk;
        break;
      case 'fp':
        splitData = savedSplits.fp;
        break;
      case 'gg_swap':
        splitData = savedSplits.gg_swap;
        break;
    }

    if (splitData) {
      const set1Count = splitData[1] || 0;
      const set2Count = splitData[2] || 0;
      return {
        set1Count,
        set2Count,
        totalModels: set1Count + set2Count
      };
    }
  }

  // Fallback to calculating from live model data
  let activeModels;
  if (splitType === 'gg_swap') {
    activeModels = models.filter(model =>
      model.account_type === 'Normal' &&
      !model.paused &&
      model.groups?.includes('GG Swap')
    );
  } else {
    activeModels = getActiveModels(models, splitType === 'uk' ? 'UK' : splitType === 'fp' ? 'FP' : undefined);
  }

  const set1Count = activeModels.filter(model => model.set === 1).length;
  const set2Count = activeModels.filter(model => model.set === 2).length;
  const totalModels = set1Count + set2Count;

  return { set1Count, set2Count, totalModels };
};

// Calculate split amounts ensuring exact total by calculating one and deriving the other
export const calculateSplitAmounts = (
  amount: number,
  bonus: number,
  set1Percentage: number
): SplitAmounts => {
  const set1Decimal = set1Percentage / 100;
  
  const set1Amount = Math.round(amount * set1Decimal * 100) / 100;
  const set1Bonus = Math.round(bonus * set1Decimal * 100) / 100;
  
  const set2Amount = Math.round((amount - set1Amount) * 100) / 100;
  const set2Bonus = Math.round((bonus - set1Bonus) * 100) / 100;
  
  return {
    set1Amount,
    set1Bonus,
    set2Amount,
    set2Bonus
  };
}; 